// app/customs/aiagent/index.tsx
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as Global from '@utils/global';
import * as AuthContext from '@/utils/authcontext';
import { useAgentView } from '@/utils/agentviewcontext';
import { setglobalUserInfo, setListeningActive, setListeningEngine, setListeningTarget } from '@/utils/speechbyapi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './index.css';

const moodIcons = ['😊', '🔍', '⏳', '🌱', '😮', '😅'];

type Agent = {
  エージェントGUID: string;
  所有社員GUID?: string;
  ニックネーム?: string;
  ベース人格GUID?: string;
  ベース人格名?: string;
  音声合成?: string;
  再生速度?: string;
  メモ?: string;
};

type AgentEvent = {
  seq: number;
  status?: string;
  role?: string;
  message?: string;
  display_flag?: number;
};

type AiAgentProps = {
  employeeGUID: string;
  employeeDomain: string;
};

const AiAgent: React.FC<AiAgentProps> = ({ employeeGUID, employeeDomain }) => {
  const { agentViewContext } = useAgentView();
  const { authToken } = AuthContext.useAuth();
  const [micEnabled, setMicEnabled] = useState(false);
  const [speakerEnabled, setSpeakerEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activeAgentGUID, setActiveAgentGUID] = useState('');
  const [activityText, setActivityText] = useState('読込中');
  const [promptText, setPromptText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [activeJobGuid, setActiveJobGuid] = useState('');
  const [lastEventSeq, setLastEventSeq] = useState(0);
  const [agentMessages, setAgentMessages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const activityRef = useRef<HTMLDivElement | null>(null);
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const finalizingJobRef = useRef<string>('');
  const moodIcon = useMemo(() => moodIcons[Math.floor(Math.random() * moodIcons.length)], []);
  const viewContextLabel = useMemo(() => {
    if (!agentViewContext) return '';
    const display = agentViewContext.display || agentViewContext.spread_id || '';
    const mode = agentViewContext.mode ? `/${agentViewContext.mode}` : '';
    return display ? `${display}${mode}` : '';
  }, [agentViewContext]);
  const buildBaseInterface = () => ({
    user_guid: employeeGUID,
    user_domain: employeeDomain,
    ip_address: typeof window === 'undefined' ? '' : window.location.hostname,
    user_agent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  });
  const activeAgent = useMemo(() => (
    agents.find((agent) => agent.エージェントGUID === activeAgentGUID)
  ), [agents, activeAgentGUID]);

  const playAgentVoice = async (text: string): Promise<boolean> => {
    const voiceType = activeAgent?.音声合成;
    if (!speakerEnabled || !voiceType || !String(text ?? '').trim()) return false;
    try {
      const speedValue = activeAgent?.再生速度 ? parseFloat(activeAgent.再生速度) : 1.0;
      const res = await axios.post<any>(
        `${Global.getApiBaseUrl()}/voice_talking/talk_by_type`,
        {
          base_interface: buildBaseInterface(),
          body_data: {
            text,
            voicetype: voiceType,
            speed: Number.isFinite(speedValue) ? speedValue : 1.0,
          },
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        if (voiceAudioRef.current.src) URL.revokeObjectURL(voiceAudioRef.current.src);
      }
      const audio = new Audio(url);
      voiceAudioRef.current = audio;
      await new Promise<void>((resolve) => {
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          audio.removeEventListener('ended', finish);
          audio.removeEventListener('error', finish);
          if (voiceAudioRef.current === audio) voiceAudioRef.current = null;
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.addEventListener('ended', finish, { once: true });
        audio.addEventListener('error', finish, { once: true });
        audio.play().catch(finish);
      });
      return true;
    } catch (err) {
      console.warn('AIエージェント音声再生失敗:', err);
      return false;
    }
  };

  useEffect(() => {
    const loadAgent = async () => {
      const baseInterface = buildBaseInterface();
      try {
        const listRes = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/myagentlist`, {
          base_interface: baseInterface,
          body_data: {},
        });
        const nextAgents = Array.isArray(listRes.data?.agents) ? listRes.data.agents : [];
        setAgents(nextAgents);
        const activeRes = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/get_activeagent`, {
          base_interface: baseInterface,
          body_data: {
            社員GUID: employeeGUID,
          },
        });
        const nextActiveAgentGUID = activeRes.data?.active_agent?.エージェントGUID ?? '';
        setActiveAgentGUID(nextActiveAgentGUID);
        setActivityText(nextActiveAgentGUID ? '待機中' : 'エージェント未選択');
      } catch {
        setAgents([]);
        setActiveAgentGUID('');
        setActivityText('読込失敗');
      }
    };
    loadAgent();
  }, [employeeGUID, employeeDomain]);

  const changeActiveAgent = async (agentGUID: string) => {
    const baseInterface = buildBaseInterface();
    setActiveAgentGUID(agentGUID);
    setActivityText('変更中');
    try {
      const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/set_activeagent`, {
        base_interface: baseInterface,
        body_data: {
          社員GUID: employeeGUID,
          エージェントGUID: agentGUID || null,
        },
      });
      setActiveAgentGUID(res.data?.active_agent?.エージェントGUID ?? '');
      setActivityText(agentGUID ? '待機中' : 'エージェント未選択');
    } catch {
      setActivityText('変更失敗');
    }
  };

  const sendPrompt = async () => {
    const prompt = (promptTextareaRef.current?.value || promptText).trim();
    if (!prompt || isSending) return;
    const token = Global.getCurrentAuthToken(authToken);
    if (!token) {
      setActivityText('ログイン確認が必要');
      return;
    }
    const nextJobGuid = Global.createClientUuid();
    setIsSending(true);
    setLastEventSeq(0);
    setAgentMessages([]);
    setActiveJobGuid(nextJobGuid);
    finalizingJobRef.current = '';
    setActivityText('送信中');
    setPromptText('');
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('user_domain', employeeDomain);
      fd.append('ip_address', window.location.hostname);
      fd.append('user_agent', navigator.userAgent);
      fd.append('prompt', prompt);
      fd.append('agent_guid', activeAgentGUID);
      fd.append('job_guid', nextJobGuid);
      if (agentViewContext) {
        fd.append('context_json', JSON.stringify(agentViewContext));
      }
      if (selectedFile) {
        fd.append('file', selectedFile);
      }
      const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agent/push`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedFile(null);
      setActivityText(res.data?.job_guid ? '応答確認中' : '完了');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setPromptText(prompt);
      setActivityText(e?.response?.data?.detail || '送信失敗');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!activeJobGuid) return;
    let stopped = false;
    const poll = async () => {
      const token = Global.getCurrentAuthToken(authToken);
      if (!token || stopped) return;
      try {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('user_domain', employeeDomain);
        fd.append('ip_address', window.location.hostname);
        fd.append('user_agent', navigator.userAgent);
        fd.append('job_guid', activeJobGuid);
        fd.append('after_seq', String(lastEventSeq));
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agent/events`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const events: AgentEvent[] = Array.isArray(res.data?.events) ? res.data.events : [];
        const latestEvent: AgentEvent | undefined = res.data?.latest_event || undefined;
        if (events.length > 0) {
          setLastEventSeq(events[events.length - 1].seq ?? lastEventSeq);
        }
        const jobStatus = res.data?.job?.status;
        const visibleEvents = events.filter((event) => event.display_flag !== 0 && event.message);
        const displayEvent = visibleEvents[visibleEvents.length - 1] || (
          latestEvent?.display_flag !== 0 && latestEvent?.message ? latestEvent : undefined
        );
        if (displayEvent?.message) {
          setAgentMessages([displayEvent.message]);
        }
        if (jobStatus) {
          setActivityText(jobStatus);
        }
        if (jobStatus === 'FINISHED' || jobStatus === 'ERROR' || jobStatus === 'CANCELED') {
          if (finalizingJobRef.current === activeJobGuid) return;
          finalizingJobRef.current = activeJobGuid;
          setActiveJobGuid('');
          if (jobStatus === 'FINISHED' && displayEvent?.message) {
            if (micEnabled) setListeningActive(false);
            await playAgentVoice(displayEvent.message);
          }
          if (jobStatus === 'FINISHED' && micEnabled) setListeningActive(true);
        }
      } catch (e: any) {
        if (e?.response?.status === 404) return;
        setActivityText(e?.response?.data?.detail || '状態取得失敗');
      }
    };
    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 1000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [activeJobGuid, authToken, employeeDomain, lastEventSeq, micEnabled, speakerEnabled, activeAgent]);

  useEffect(() => {
    setglobalUserInfo(employeeGUID, employeeDomain);
  }, [employeeGUID, employeeDomain]);

  useEffect(() => {
    if (!micEnabled) return;
    setListeningTarget('aiagent-main-textbox', 'aiagent-send-button', 'aiagent-rec-indicator');
    setglobalUserInfo(employeeGUID, employeeDomain);
    setListeningEngine(true);
    setListeningActive(true);
    return () => {
      setListeningActive(false);
      setListeningEngine(false);
    };
  }, [micEnabled, employeeGUID, employeeDomain]);

  useEffect(() => {
    if (!activityRef.current) return;
    activityRef.current.scrollTop = activityRef.current.scrollHeight;
  }, [activityText, agentMessages, viewContextLabel]);

  return (
    <section className="aiagent-widget" aria-label="AIエージェント">
      <div className="aiagent-header">
        <span className="aiagent-badge">AGENT</span>
        <span className="aiagent-mood" aria-hidden="true">{moodIcon}</span>
        <select
          className="aiagent-select"
          value={activeAgentGUID}
          aria-label="エージェント選択"
          onChange={(e) => changeActiveAgent(e.target.value)}
        >
          <option value="">未選択</option>
          {agents.map((agent) => (
            <option key={agent.エージェントGUID} value={agent.エージェントGUID}>
              {agent.ニックネーム || agent.エージェントGUID}
            </option>
          ))}
        </select>
      </div>
      <div ref={activityRef} className="aiagent-activity" aria-label="アクティビティ">
        <div>{activityText}</div>
        {agentMessages.map((message, index) => (
          <div key={`${index}-${message}`} className="aiagent-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({ children }) => (
                  <table className="aiagent-md-table">{children}</table>
                ),
                th: ({ children }) => <th className="aiagent-md-th">{children}</th>,
                td: ({ children }) => <td className="aiagent-md-td">{children}</td>,
              }}
            >
              {message}
            </ReactMarkdown>
          </div>
        ))}
        {viewContextLabel && <small>{viewContextLabel}</small>}
      </div>
      <div className="aiagent-tools" aria-label="エージェント操作">
        <button
          type="button"
          className={micEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="マイク"
          aria-pressed={micEnabled}
          onClick={() => setMicEnabled((v) => !v)}
        >
          <i className="fa-solid fa-microphone" />
          <span id="aiagent-rec-indicator" className="aiagent-rec-indicator" />
        </button>
        <button
          type="button"
          className={speakerEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="スピーカー"
          aria-pressed={speakerEnabled}
          onClick={() => setSpeakerEnabled((v) => !v)}
        >
          <i className="fa-solid fa-volume-high" />
        </button>
        <button
          type="button"
          className={cameraEnabled ? 'aiagent-tool active' : 'aiagent-tool'}
          title="カメラ"
          aria-pressed={cameraEnabled}
          onClick={() => setCameraEnabled((v) => !v)}
        >
          <i className="fa-solid fa-camera" />
        </button>
        <button type="button" className="aiagent-tool" title="承認">
          <i className="fa-solid fa-check-to-slot" />
        </button>
        <button
          type="button"
          className={selectedFile ? 'aiagent-tool active' : 'aiagent-tool'}
          title="添付"
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="fa-solid fa-paperclip" />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="aiagent-file-input"
        onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
      />
      <textarea
        ref={promptTextareaRef}
        id="aiagent-main-textbox"
        className="aiagent-input"
        rows={5}
        placeholder="ソフィアへ依頼..."
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
      />
      <button
        id="aiagent-send-button"
        type="button"
        className="aiagent-send"
        title="送信"
        disabled={isSending}
        onClick={sendPrompt}
      >
        <i className="fa-solid fa-paper-plane" />
      </button>
    </section>
  );
};

export default AiAgent;
